import {
  DashboardEconomyChangeCategory,
  DashboardEconomyChangeItem,
  DashboardEconomyChangesMeasure,
  DashboardEconomyTimelineEntry,
} from '@interfaces/dashboard/responses/dashboard-economy-changes.response';
import {
  ECONOMY_CHANGE_CATEGORY_LABELS,
  ECONOMY_CHANGE_CATEGORY_ORDER,
  isAutomaticEconomyTrigger,
} from '@src/app/features/dashboard/dashboard-management/pages/economy-history/economy-history.labels';

/** Valor abaixo disso (meio centavo) conta como zero. */
const ZERO = 0.005;

export function isZeroAmount(value: number | null | undefined): boolean {
  return value === null || value === undefined || Math.abs(value) < ZERO;
}

/** Peças da frase do "O que mudou" (card 3323). O valor vai pelo pipe de moeda no template. */
export interface EconomyChangeSummary {
  direction: 'down' | 'up' | 'flat';
  amount: number;
  /** Variação sobre a economia do início, em %; nulo quando o início é zero. */
  percent: number | null;
  /** "toda em Mercado e demais" / "principalmente em Aditamentos"; nulo quando não há variação. */
  focus: string | null;
  /** Quantos eventos (motivos que não são rodada automática) houve no período. */
  eventCount: number;
}

export function buildEconomyChangeSummary(
  measure: DashboardEconomyChangesMeasure,
  timeline: DashboardEconomyTimelineEntry[]
): EconomyChangeSummary {
  const variation = measure.variation ?? 0;
  const eventCount = (timeline ?? []).filter(e => !isAutomaticEconomyTrigger(e.trigger)).length;

  if (isZeroAmount(variation)) {
    return { direction: 'flat', amount: 0, percent: null, focus: null, eventCount };
  }

  const nonZero = changedItems(measure);
  let focus: string | null = null;
  if (nonZero.length === 1) {
    focus = `toda em ${ECONOMY_CHANGE_CATEGORY_LABELS[nonZero[0].category]}`;
  } else if (nonZero.length > 1) {
    const top = [...nonZero].sort((a, b) => Math.abs(b.value) - Math.abs(a.value))[0];
    focus = `principalmente em ${ECONOMY_CHANGE_CATEGORY_LABELS[top.category]}`;
  }

  return {
    direction: variation > 0 ? 'up' : 'down',
    amount: Math.abs(variation),
    percent: isZeroAmount(measure.start) ? null : (variation / Math.abs(measure.start)) * 100,
    focus,
    eventCount,
  };
}

/** Itens com variação, na ordem de exibição. */
export function changedItems(
  measure: DashboardEconomyChangesMeasure
): DashboardEconomyChangeItem[] {
  return sortByCategoryOrder((measure.items ?? []).filter(i => !isZeroAmount(i.value)));
}

/** Categorias sem mudança no período, para a linha "Sem mudança no período". */
export function unchangedCategories(
  measure: DashboardEconomyChangesMeasure
): DashboardEconomyChangeCategory[] {
  return sortByCategoryOrder((measure.items ?? []).filter(i => isZeroAmount(i.value)))
    .map(i => i.category)
    .filter(c => c !== 'Rules');
}

function sortByCategoryOrder(items: DashboardEconomyChangeItem[]): DashboardEconomyChangeItem[] {
  const rank = (c: DashboardEconomyChangeCategory) => {
    const i = ECONOMY_CHANGE_CATEGORY_ORDER.indexOf(c);
    return i < 0 ? 99 : i;
  };
  return [...items].sort((a, b) => rank(a.category) - rank(b.category));
}
